import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormScriptResponse } from '~/models/Commons ';
import { SIMPLDialog } from '../../components/commons/dialog/SimplDialog';
import { SIMPLSelectDropdown } from '../../components/commons/input/SIMPLSelectDropdown';
import { SIMPLButtonCustomized } from '../../components/commons/button/SIMPLButtonCustumized';
import { LoadingWrapper } from '../../components/commons/spiner/SimpleLoaderWrapper';
import { useGetOutputTemplatesQuery } from '~/services/outputTemplate';
import { useFormScriptOutputTemplate } from './UseFormScriptOutputTemplate';

interface Prop {
  onCloseModal: () => void;
  item: FormScriptResponse;
  refetch: () => void;
  onSuccess?: (title: string, message: string, response: any) => void;
  onError?: (title: string, message: string) => void;
}

const labelClass = 'font-semibold max-w-[300px]';

const ScriptOutputTemplateModal = ({
  onCloseModal,
  item,
  refetch,
  onSuccess,
  onError,
}: Prop) => {
  const { t } = useTranslation();

  const {
    handleSubmit,
    control,
    errors,
    isLoading,
    isError,
    isChanged,
    submitForm,
  } = useFormScriptOutputTemplate(item, onCloseModal, refetch, onSuccess, onError);

  const {
    data: outputTemplatesData,
    isLoading: isOTLoading,
    isError: isOTError,
  } = useGetOutputTemplatesQuery({
    page: 0,
    size: 9999,
    active: true,
  });

  const otOptions = (outputTemplatesData?.payload?.content ?? []).map((ot: any) => ({
    label: ot.name,
    value: ot.id,
  }));

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    handleSubmit(submitForm)(event);
  };

  const renderButtons = () => (
    <>
      {!isLoading && (
        <div className="grow-0 basis-32 mr-5">
          <SIMPLButtonCustomized
            label={t('Cancel')}
            secondary
            buttonHandlerAction={onCloseModal}
            testId="output-template-modal-button-cancel"
          />
        </div>
      )}
      {!isLoading && (
        <div className="grow-0 basis-32">
          <SIMPLButtonCustomized
            label={t('Submit')}
            primary
            disabled={!isChanged}
            testId="output-template-modal-button-submit"
          />
        </div>
      )}
    </>
  );

  return (
    <SIMPLDialog
      onCloseModal={onCloseModal}
      title={t('OutputTemplate')}
      actions={renderButtons()}
      onSubmit={handleFormSubmit}
      showLine
    >
      <LoadingWrapper
        loadingTitle={t('LoadingOutputTemplates')}
        isLoading={isOTLoading}
        isError={isOTError}
        testId="output-template-modal-loading"
      >
        <div className="w-auto pb-3 flex flex-col">
          <div className="flex flex-row mt-3 p-3">
            <div className="flex-1">
              <div className={labelClass}>{t('Title')}</div>
              <div>{item.title}</div>
            </div>
          </div>

          <div className="flex flex-row mt-3 p-3">
            {item.cloudEnvironment?.cloudProvider?.cloudProviderName && (
              <div className="flex-1">
                <div className={labelClass}>{t('CloudProvider')}</div>
                <div>{item.cloudEnvironment.cloudProvider.cloudProviderName}</div>
              </div>
            )}
          </div>

          <div className="flex flex-row mt-3 pb-2">
            <div className="flex-1 p-3">
              <Controller
                name="outputTemplateId"
                control={control}
                rules={{ required: t('MessageOutputTemplateRequired') }}
                render={({ field }) => {
                  const isValueValid = field.value
                    ? otOptions.some((opt) => opt.value === field.value?.value)
                    : false;
                  return (
                  <SIMPLSelectDropdown
                    id="outputTemplateId"
                    label={t('OutputTemplate')}
                    options={otOptions}
                    isRequired
                    value={isValueValid ? field.value : null}
                    onChange={field.onChange}
                    hideErrorLabel={false}
                    errorLabel={errors.outputTemplateId?.message as string}
                    placeholder={t('OutputTemplate')}
                    background="white"
                    testId="output-template-modal-dropdown"
                  />
                  );
                }}
              />
            </div>
          </div>
        </div>
      </LoadingWrapper>
    </SIMPLDialog>
  );
};

export default ScriptOutputTemplateModal;
